import RefreshIcon from '@mui/icons-material/Refresh';
import { Alert, Box, Button, CircularProgress, Paper, Typography } from '@shared/ui';
import { useGetIdentityResolutionReportQuery } from '../api/identityResolutionApi';

function StatRow({ label, value }: { label: string; value: number }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 2,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {value.toLocaleString()}
      </Typography>
    </Box>
  );
}

function StatSection({
  title,
  rows,
}: {
  title: string;
  rows: Array<{ label: string; value: number }>;
}) {
  return (
    <Box sx={{ display: 'grid', gap: 1 }}>
      <Typography variant="subtitle2">{title}</Typography>
      {rows.map((row) => (
        <StatRow key={row.label} label={row.label} value={row.value} />
      ))}
    </Box>
  );
}

export default function IdentityResolutionCard() {
  const { data, error, isLoading, isFetching, refetch } = useGetIdentityResolutionReportQuery();

  return (
    <Paper variant="outlined" sx={{ p: 3, maxWidth: 640 }}>
      <Box
        sx={{
          alignItems: 'flex-start',
          display: 'flex',
          gap: 2,
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" gutterBottom>
            Identity resolution
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review legacy identity coverage, Typeform resolution quality, and capacity-sheet
            resolution from the main admin area before or after rollout.
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={() => void refetch()}
          loading={isFetching}
          sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          Refresh
        </Button>
      </Box>

      {isLoading && (
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5, minHeight: 64 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading identity-resolution stats...
          </Typography>
        </Box>
      )}

      {error && !isLoading && (
        <Alert severity="error">Failed to load identity-resolution stats.</Alert>
      )}

      {data && !isLoading && (
        <Box sx={{ display: 'grid', gap: 2.5 }}>
          <StatSection
            title="Legacy app identity coverage"
            rows={[
              {
                label: 'Total active people',
                value: data.legacy_app_identity_coverage.total_people,
              },
              {
                label: 'Mapped to legacy_app',
                value: data.legacy_app_identity_coverage.mapped_people,
              },
              { label: 'Not mapped yet', value: data.legacy_app_identity_coverage.unmapped_people },
            ]}
          />

          <StatSection
            title="Typeform linkage quality"
            rows={[
              {
                label: 'Total submissions',
                value: data.typeform_linkage_quality.total_submissions,
              },
              {
                label: 'Direct linked person',
                value: data.typeform_linkage_quality.direct_linked_person,
              },
              {
                label: 'Resolved by email',
                value: data.typeform_linkage_quality.resolved_by_email,
              },
              { label: 'Still unresolved', value: data.typeform_linkage_quality.unresolved },
            ]}
          />

          {data.capacity_sheet_resolution && (
            <StatSection
              title="Capacity-sheet resolution"
              rows={[
                { label: 'Total rows', value: data.capacity_sheet_resolution.total_rows },
                {
                  label: 'Direct legacy ID',
                  value: data.capacity_sheet_resolution.direct_legacy_id,
                },
                {
                  label: 'Coach-name fallback',
                  value: data.capacity_sheet_resolution.coach_name_fallback,
                },
                { label: 'Still unresolved', value: data.capacity_sheet_resolution.unresolved },
              ]}
            />
          )}

          {data.capacity_sheet_resolution_error && (
            <Alert severity="warning">{data.capacity_sheet_resolution_error}</Alert>
          )}
        </Box>
      )}
    </Paper>
  );
}
